import { format } from 'date-fns';

/**
 * Canonical unambiguous date format for Store Control: "10 Jul 2026".
 * Named month + day-first reads correctly for US, UK, and EU users — never
 * use numeric MM/DD or DD/MM orderings in the UI.
 */
export const DATE_FORMAT = 'd MMM yyyy';
export const DATE_FORMAT_NO_YEAR = 'd MMM';
/** Text-field format for MUI date/datetime pickers ("10 Jul 2026"). */
export const PICKER_DATE_FORMAT = 'd MMM yyyy';
export const PICKER_DATE_TIME_FORMAT = 'd MMM yyyy hh:mm a';

/** Format a date as the canonical unambiguous "10 Jul 2026". */
export function formatDayMonthYear(date: Date): string {
  return format(date, DATE_FORMAT);
}

/**
 * Format a timestamp as a relative time string (e.g., "2 hours ago", "Yesterday")
 */
export function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Check if same day
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  // Check if yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isToday) {
    if (diffMinutes < 1) {
      return 'Just now';
    }
    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    }
    // Format as time for today
    return `Today ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }

  if (isYesterday) {
    return `Yesterday ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }

  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }

  // For older dates, show the full date
  return format(date, date.getFullYear() !== now.getFullYear() ? DATE_FORMAT : DATE_FORMAT_NO_YEAR);
}

/**
 * Format a date for display in day groups (e.g., "Today", "Yesterday", "Jan 15")
 */
export function formatDateGroup(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();

  // Check if same day
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (isToday) {
    return 'Today';
  }

  // Check if yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isYesterday) {
    return 'Yesterday';
  }

  // For other dates
  return format(
    date,
    date.getFullYear() !== now.getFullYear()
      ? `EEEE, ${DATE_FORMAT}`
      : `EEEE, ${DATE_FORMAT_NO_YEAR}`
  );
}

/**
 * Format a date/time for snooze and channel expiry displays, respecting the
 * user's 24-hour preference, using the canonical unambiguous date format.
 */
export function formatSnoozeDateTime(date: Date, use24Hour: boolean): string {
  return format(date, use24Hour ? `${DATE_FORMAT}, HH:mm` : `${DATE_FORMAT}, h:mm a`);
}

/**
 * Get date key for grouping (YYYY-MM-DD format)
 */
export function getDateKey(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toISOString().split('T')[0];
}

/**
 * Group logs by date
 */
export function groupLogsByDate<T extends { timestamp: string }>(logs: T[]): Map<string, T[]> {
  const groups = new Map<string, T[]>();

  for (const log of logs) {
    const key = getDateKey(log.timestamp);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(log);
  }

  return groups;
}
